import { useEffect, type ReactNode } from "react";
import { ConfidenceBadge, MeasureBadge } from "./badges.js";

/**
 * The common screen pattern (Req 27): title → scope → KPIs → filters →
 * visualization → data table → evidence/action drawer. These primitives keep
 * every CoreFinOps screen consistent instead of duplicating layout per screen.
 */

export function ScreenLayout(props: {
  title: string;
  scope?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <section style={{ padding: "1.75rem 2rem", maxWidth: 1320, margin: "0 auto" }}>
      <header style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, marginBottom: "1.25rem" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "1.6rem", fontWeight: 700, letterSpacing: "-0.02em", color: "#17212b" }}>{props.title}</h1>
          {props.scope ? <div style={{ color: "#566472", fontSize: "0.9rem", marginTop: 4 }}>{props.scope}</div> : null}
        </div>
        {props.actions ? <div>{props.actions}</div> : null}
      </header>
      <div>{props.children}</div>
    </section>
  );
}

export function KpiGrid({ children }: { children: ReactNode }): ReactNode {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>{children}</div>
  );
}

export function KpiCard(props: {
  label: string;
  value: ReactNode;
  measure?: string | null | undefined;
  confidence?: string | null | undefined;
  sub?: ReactNode;
}): ReactNode {
  return (
    <div
      style={{
        border: "1px solid #e7ecf2",
        borderRadius: 12,
        padding: "1.1rem 1.15rem",
        background: "#fff",
        boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
      }}
    >
      <div style={{ fontSize: "0.72rem", color: "#566472", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>{props.label}</div>
      <div style={{ fontSize: "1.75rem", fontWeight: 700, marginTop: 6, color: "#17212b", lineHeight: 1.1 }}>{props.value}</div>
      {props.sub ? <div style={{ fontSize: "0.85rem", color: "#5f6c79", marginTop: 4 }}>{props.sub}</div> : null}
      {(props.measure || props.confidence) && (
        <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
          <MeasureBadge measure={props.measure} />
          <ConfidenceBadge label={props.confidence} />
        </div>
      )}
    </div>
  );
}

/** A right-side evidence/action drawer (Req 27). Controlled by `open`. */
export function Drawer(props: { open: boolean; title: string; onClose: () => void; children: ReactNode }): ReactNode {
  const { open, onClose } = props;
  // Escape closes the dialog (keyboard accessibility).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div
        onClick={props.onClose}
        aria-hidden
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 40 }}
      />
      <aside
        role="dialog"
        aria-label={props.title}
        aria-modal="true"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(480px, 92vw)",
          background: "#fff",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.18)",
          zIndex: 50,
          overflowY: "auto",
          padding: "1.25rem",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: "1.15rem" }}>{props.title}</h2>
          <button type="button" onClick={props.onClose} aria-label="Close drawer">
            ✕
          </button>
        </div>
        <div style={{ marginTop: "1rem" }}>{props.children}</div>
      </aside>
    </>
  );
}
